/**
 * @satchel/core Configuration
 *
 * Shared configuration constants for SATCHEL core functionality.
 * These values can be imported by consuming apps (PWA, Electron) or overridden via siteMetadata.
 *
 * IMPORTANT: This is the single source of truth for SATCHEL API configuration.
 * Do not hardcode URLs or ports elsewhere - import from here.
 */

import type { BitcoinNetwork } from "./types";

/**
 * RGB Infrastructure polling interval in milliseconds.
 * Controls how often the wallet hook checks API connectivity status.
 * Default: 60 seconds
 */
export const RGB_INFRA_POLL_INTERVAL = 60000;

const BITCOIN_NETWORKS: readonly BitcoinNetwork[] = [
  "mainnet",
  "testnet4",
  "testnet",
  "signet",
  "regtest",
];

/** The Bitcoin network a new SATCHEL is created on: `VITE_BITCOIN_NETWORK`, regtest when unset. */
export const BITCOIN_NETWORK: BitcoinNetwork = (() => {
  let configured: string | undefined;
  try {
    configured = (import.meta.env?.VITE_BITCOIN_NETWORK as string | undefined)?.trim();
  } catch {
    // import.meta.env does not exist outside a Vite build
  }
  if (!configured) return "regtest";
  if (!(BITCOIN_NETWORKS as readonly string[]).includes(configured)) {
    throw new Error(
      `VITE_BITCOIN_NETWORK must be one of ${BITCOIN_NETWORKS.join(", ")}, got "${configured}"`,
    );
  }
  return configured as BitcoinNetwork;
})();

/**
 * SATCHEL API Configuration
 *
 * Default configuration for the satchel serve HTTP API.
 */
export const SATCHEL_API_CONFIG = {
  /** Default port for satchel serve */
  DEFAULT_PORT: 3003,

  /** Default host for local development */
  DEFAULT_HOST: "localhost",

  /** Address of a local satchel serve, and of the desktop app's embedded server */
  get DEFAULT_API_URL(): string {
    return `http://${this.DEFAULT_HOST}:${this.DEFAULT_PORT}/api`;
  },

  /**
   * Where a browser reaches the API: a same-origin path that the dev server or
   * the hosting platform forwards to rgb-satchel with the API token added.
   */
  WEB_API_URL: "/backend",

  /** Default request timeout in milliseconds (60s for asset issuance which can take 30-45s) */
  DEFAULT_TIMEOUT: 60000,
} as const;

/**
 * RGB-Lib API Configuration
 *
 * Configuration for the rgb-lib-server HTTP API (AluVM-based RGB wallet).
 * Separate from SATCHEL_API_CONFIG — runs on its own port with its own API shape.
 */
export const RGB_LIB_API_CONFIG = {
  /** Default port for rgb-lib-server */
  DEFAULT_PORT: 3004,

  /** Default host for local development */
  DEFAULT_HOST: "localhost",

  /** Default API base URL (development) */
  get DEFAULT_API_URL(): string {
    return `http://${this.DEFAULT_HOST}:${this.DEFAULT_PORT}/api`;
  },

  /**
   * Where a browser reaches the API: a same-origin path that the dev server or
   * the hosting platform forwards to rgb-lib-server with the API token added.
   */
  WEB_API_URL: "/rgblib",

  /** Default request timeout in milliseconds (120s for RGB operations which can be slow) */
  DEFAULT_TIMEOUT: 120000,
} as const;

/**
 * Environment-specific API URLs
 * Used by siteMetadata and other configuration consumers.
 */
export const API_ENVIRONMENTS = {
  development: {
    apiUrl: SATCHEL_API_CONFIG.DEFAULT_API_URL,
    debug: true,
  },
  test: {
    apiUrl: "https://test-api.smart-assets.io",
    debug: true,
  },
  production: {
    apiUrl: "https://api.smart-assets.io",
    debug: false,
  },
} as const;

/**
 * Wallet Storage Keys
 *
 * Centralized storage keys for wallet-related session data.
 * These keys are used with sessionStorage for temporary credential storage.
 *
 * SECURITY NOTE: sessionStorage is cleared when the browser tab closes.
 * This is intentional for security - credentials should not persist across sessions.
 */
export const WALLET_STORAGE_KEYS = {
  /** Key for storing the wallet password in sessionStorage */
  PASSWORD: "satchel_wallet_password",

  /** Key for storing the API wallet name in localStorage (unique identifier) */
  WALLET_NAME: "satchel_wallet_name",

  /** Key for storing the selected network in localStorage */
  NETWORK: "satchel_selected_network",
} as const;

/**
 * Generate a unique wallet name for API use.
 * Uses timestamp + random suffix to ensure uniqueness across users.
 *
 * @returns A unique wallet identifier like "w_1704567890123_a1b2"
 */
export const generateUniqueWalletName = (): string => {
  const timestamp = Date.now();
  const randomSuffix = Math.random().toString(36).substring(2, 6);
  return `w_${timestamp}_${randomSuffix}`;
};

/**
 * Error Messages
 *
 * Centralized error messages for consistent user feedback across the application.
 * These messages are used in hooks, contexts, and UI components.
 */
export const ERROR_MESSAGES = {
  /** Connection error message for API not reachable */
  CONNECTION_ERROR:
    "SATCHEL API not reachable. Check that `satchel serve` is running and that SATCHEL_API_URL points at it.",

  /** Generic balance load failure */
  WALLET_LOAD_FAILED: "Failed to load balances",

  /** No SATCHEL has been set up on this device */
  NOT_SET_UP: "SATCHEL is not set up",

  /** Incorrect password (decryption failed) */
  INCORRECT_PASSWORD: "Incorrect password. Please try again.",

  /** Another program (a CLI command or a second server) has this SATCHEL open */
  IN_USE: "This SATCHEL is open in another program. Close it there, then try again.",
} as const;

/**
 * Static asset paths, from the root of the origin that serves the wallet page
 * (the web server, the dev server, or the desktop app's `satchel://wallet`).
 */
export const ASSET_PATHS = {
  /** Smart Assets directory base path */
  SMART_ASSETS_BASE: "/assets/smart-assets",

  /** SATCHEL branding assets */
  SATCHEL_LOGO: "/Satchel_192px.png",

  /** Smart Assets brain logo (used next to SA Credits) */
  SA_BRAIN_LOGO: "/assets/smart-assets/SA_brain_logo.png",

  /** PAIR token icon */
  PAIR_ICON: "/assets/smart-assets/PAIR.png",

  /** Helper to construct Smart Asset icon paths */
  smartAssetIcon: (filename: string): string => `/assets/smart-assets/${filename}`,

  /** Helper to construct value flows diagram paths */
  valueFlowsDiagram: (filename: string): string => `/assets/smart-assets/value-flows/${filename}`,
} as const;
